import { RouterProvider, createRouter, createRoute, createRootRoute, Outlet, redirect } from '@tanstack/react-router';
import { AppShell } from './components/layout/AppShell.js';
import { LoginPage } from './routes/login.js';
import { DashboardPage } from './routes/dashboard.js';
import { TrendsPage } from './routes/trends.js';
import { PulsePage } from './routes/pulse.js';
import { CalendarPage } from './routes/calendar.js';
import { TechniciansPage } from './routes/technicians.js';
import { TimesheetsPage } from './routes/timesheets.js';
import { PayslipPage } from './routes/payslip.js';
import { PayoutsPage } from './routes/payouts.js';
import { CreativesPage } from './routes/creatives.js';
import { LeadsPage } from './routes/leads.js';
import { BookingsPage } from './routes/bookings.js';
import { AdvisorPage } from './routes/advisor.js';
import { UploadPage } from './routes/upload.js';
import { PipelinePage } from './routes/pipeline.js';
import { SettingsPage } from './routes/settings.js';

const rootRoute = createRootRoute({ component: Outlet });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (s: Record<string, unknown>) => ({ redirect: s['redirect'] as string | undefined }),
  component: LoginPage,
});

function ProtectedLayout() {
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  beforeLoad: async ({ location }) => {
    const res = await fetch('/api/trpc/auth.me?batch=1&input=%7B%220%22%3A%7B%22json%22%3Anull%7D%7D', {
      credentials: 'include',
    });
    const data = await res.json() as [{ result: { data: { json: unknown } } }];
    if (!data[0]?.result?.data?.json) {
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
  },
  component: ProtectedLayout,
});

const indexRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/',
  beforeLoad: () => { throw redirect({ to: '/dashboard' }); },
  component: () => null,
});

const dashboardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/dashboard',
  component: DashboardPage,
});

const trendsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/trends',
  component: TrendsPage,
});

const pulseRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/pulse',
  component: PulsePage,
});

const calendarRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/calendar',
  component: CalendarPage,
});

const techniciansRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/technicians',
  component: TechniciansPage,
});

const timesheetsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/timesheets',
  component: TimesheetsPage,
});

const payslipRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/payslip',
  validateSearch: (s: Record<string, unknown>) => ({
    tech: String(s['tech'] ?? ''),
    weekStart: String(s['weekStart'] ?? ''),
    autoprint: s['autoprint'] === '1' ? '1' as const : undefined,
  }),
  component: PayslipPage,
});

const payoutsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/payouts',
  component: PayoutsPage,
});

const creativesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/creatives',
  component: CreativesPage,
});

const creativeDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/creatives/$id',
  component: () => <div className="p-4 text-gray-500">Creative detail view — coming soon</div>,
});

const leadsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/leads',
  component: LeadsPage,
});

const bookingsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/bookings',
  component: BookingsPage,
});

const advisorRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/advisor',
  component: AdvisorPage,
});

const uploadRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/upload',
  component: UploadPage,
});

const pipelineRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/pipeline',
  component: PipelinePage,
});

const settingsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings',
  component: SettingsPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  protectedRoute.addChildren([
    indexRoute,
    dashboardRoute,
    pulseRoute,
    calendarRoute,
    techniciansRoute,
    timesheetsRoute,
    payslipRoute,
    payoutsRoute,
    trendsRoute,
    creativesRoute,
    creativeDetailRoute,
    leadsRoute,
    bookingsRoute,
    advisorRoute,
    uploadRoute,
    pipelineRoute,
    settingsRoute,
  ]),
]);

const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export function App() {
  return <RouterProvider router={router} />;
}
